import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAccessToken, useSubjectLanguageVariants } from '@features/user';
import {
  useOrganizedPatientCoverages,
  useCreatePatientCoverageMutation,
  useUpdatePatientCoverageMutation,
  useDeletePatientCoverageMutation,
  mapInsuranceToCoverageRequest,
  type PatientCoverage,
  type ClientErrorResponse,
} from '@features/patient-coverages';
import AddIcon from '@mui/icons-material/Add';
import ContactEmergencyIcon from '@mui/icons-material/ContactEmergency';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HomeIcon from '@mui/icons-material/Home';
import PaymentIcon from '@mui/icons-material/Payment';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import WorkIcon from '@mui/icons-material/Work';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { matchIsValidTel, MuiTelInput } from 'mui-tel-input';
import { useSnackbar } from 'notistack';

import { AutosaveStatus } from '../AutosaveStatus';
import { useAutoSave } from '../../hooks/useAutoSave';
import APIService from '../../services/APIService';
import SentryService from '../../services/SentryService';
import {
  type EmergencyContact,
  type InsuranceInfo,
  type Patient,
  type PatientAddress,
} from '../../types/types';
import {
  useCommunicationsPreferencesQuery,
  useUpdateLanguagePreferenceMutation,
} from '@features/patients/store/api';

import AddressCard from './AddressCard';
import EmergencyContactCard from './EmergencyContactCard';
import InsuranceCard from './InsuranceCard';
import { PatientCommunicationPreferences } from './PatientCommunicationPreferences';
import { patientFieldValidators, patientValidationSchema } from './patientValidation';

// Supported languages with their display names
const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
] as const;

type SectionKey = 'demographics' | 'contact' | 'addresses' | 'insurance' | 'emergency' | 'employment';

interface PatientInformationViewProps {
  patientId: string;
  highlightedFields?: string[];
  onPatientSave?: (updatedPatient: Patient) => void;
  /** Fires after a primary/secondary/tertiary coverage create, update, or delete succeeds. */
  onCoverageSave?: () => void;
  initialExpandedSections?: Partial<Record<SectionKey, boolean>>;
  /** When set, auto-scrolls to this section after patient data loads */
  scrollToSection?: SectionKey;
}

const PatientInformationView: React.FC<PatientInformationViewProps> = ({
  patientId,
  highlightedFields: highlightedFieldsProp = [],
  onPatientSave,
  onCoverageSave,
  initialExpandedSections,
  scrollToSection,
}) => {
  const [activeHighlights, setActiveHighlights] = useState<string[]>(highlightedFieldsProp);

  // Sync when prop changes (e.g. sidebar re-opened with different fields)
  useEffect(() => {
    setActiveHighlights(highlightedFieldsProp);
  }, [highlightedFieldsProp]);

  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(true);
  const sectionRefs = useRef<Partial<Record<SectionKey, HTMLDivElement | null>>>({});
  const hasAutoScrolledRef = useRef(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [selectedPayer, setSelectedPayer] = useState<{
    id: string;
    name: string;
    eligibility: string;
  } | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // State for collapsible sections
  const [sectionsExpanded, setSectionsExpanded] = useState({
    demographics: true,
    contact: true,
    addresses: false,
    insurance: false,
    emergency: false,
    employment: false,
    ...initialExpandedSections,
  });

  const setSectionRef = useCallback(
    (section: SectionKey) => (element: HTMLDivElement | null) => {
      sectionRefs.current[section] = element;
    },
    [],
  );

  // Auto-scroll to target section after patient data loads
  useEffect(() => {
    if (!scrollToSection) {
      hasAutoScrolledRef.current = false;
      return;
    }

    if (hasAutoScrolledRef.current || loading || !patient) {
      return;
    }

    const scrollTarget = scrollToSection ? sectionRefs.current[scrollToSection] : null;
    if (!scrollTarget) {
      return;
    }

    hasAutoScrolledRef.current = true;
    // Small delay to allow the section to expand before scrolling
    const timer = setTimeout(() => {
      scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => clearTimeout(timer);
  }, [scrollToSection, loading, patient]);

  useEffect(() => {
    hasAutoScrolledRef.current = false;
  }, [patientId, scrollToSection]);

  // State for showing additional fields
  const [showSecondaryAddress, setShowSecondaryAddress] = useState(false);
  const [showSecondaryInsurance, setShowSecondaryInsurance] = useState(false);
  const [showTertiaryInsurance, setShowTertiaryInsurance] = useState(false);

  // Track pending coverage changes for the AutosaveStatus indicator
  const [hasPendingCoverageChanges, setHasPendingCoverageChanges] = useState(false);

  // Local coverage state for debouncing
  const [localCoverages, setLocalCoverages] = useState<{
    primary?: InsuranceInfo | PatientCoverage | null;
    secondary?: InsuranceInfo | PatientCoverage | null;
    tertiary?: InsuranceInfo | PatientCoverage | null;
  }>({});

  const { enqueueSnackbar } = useSnackbar();
  const { getAccessToken } = useAccessToken();
  const subjectLanguageVariants = useSubjectLanguageVariants();

  // Refs for tracking changes
  const originalPatientRef = useRef<Patient | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const statusTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch patient coverages separately
  const {
    organized: organizedCoverages,
    isLoading: coveragesLoading,
    refetch: refetchCoverages,
  } = useOrganizedPatientCoverages(patientId);

  // Coverage mutations
  const [createCoverage] = useCreatePatientCoverageMutation();
  const [updateCoverage] = useUpdatePatientCoverageMutation();
  const [deleteCoverage] = useDeletePatientCoverageMutation();

  // Language preference
  const { data: communicationsPreferences, refetch: refetchCommunicationsPreferences } = useCommunicationsPreferencesQuery({ patientId });
  const [updateLanguage, { isLoading: isUpdatingLanguage }] = useUpdateLanguagePreferenceMutation();

  useEffect(() => {
    fetchPatient();
  }, [patientId]);

  // Sync local coverages from fetched data and set visibility
  useEffect(() => {
    if (!coveragesLoading) {
      setLocalCoverages({
        primary: organizedCoverages.primary,
        secondary: organizedCoverages.secondary,
        tertiary: organizedCoverages.tertiary,
      });

      if (organizedCoverages.secondary) {
        setShowSecondaryInsurance(true);
      }
      if (organizedCoverages.tertiary) {
        setShowTertiaryInsurance(true);
      }

      // Clear pending changes flag after sync
      setHasPendingCoverageChanges(false);
    }
  }, [organizedCoverages, coveragesLoading]);

  const handleSelectChange = (event: SelectChangeEvent) => {
    const { name, value } = event.target;
    setPatient(prev => {
      if (!prev) return null;
      const updatedPatient = { ...prev, [name]: value };
      triggerAutosave(updatedPatient);
      return updatedPatient;
    });
  };

  const handlePayerSelect = (payer: { id: string; name: string; eligibility: string } | null) => {
    if (payer) {
      setSelectedPayer(payer);
      setPatient(prev => {
        if (!prev) return null;
        const updatedPatient = {
          ...prev,
          payer_id: payer.id,
          payer_name: payer.name,
          eligibility: payer.eligibility,
        };
        triggerAutosave(updatedPatient);
        return updatedPatient;
      });
    } else {
      setSelectedPayer(null);
      setPatient(prev => {
        if (!prev) return null;
        const updatedPatient = { ...prev, payer_id: '', payer_name: '', eligibility: '' };
        triggerAutosave(updatedPatient);
        return updatedPatient;
      });
    }
  };

  const fetchPatient = async () => {
    setLoading(true);
    try {
      const accessToken = await getAccessToken();
      const response = await APIService.makeAPIGetRequest({
        requestString: `/patient/get?patient_id=${patientId}&origin=PatientInformationView`,
        accessToken,
      });

      if (response.ok) {
        const data = await response.value;
        setPatient(data.patient);
        originalPatientRef.current = data.patient;

        // Check for new structure first, then fall back to legacy
        if (
          data.patient.primary_insurance?.payer_id &&
          data.patient.primary_insurance?.payer_name
        ) {
          setSelectedPayer({
            id: data.patient.primary_insurance.payer_id,
            name: data.patient.primary_insurance.payer_name,
            eligibility: '',
          });
        } else if (data.patient.payer_id && data.patient.payer_name) {
          setSelectedPayer({
            id: data.patient.payer_id,
            name: data.patient.payer_name,
            eligibility: data.patient.eligibility || '',
          });
        }

        // Show additional sections if data exists
        if (data.patient.secondary_address) {
          setShowSecondaryAddress(true);
        }
        if (data.patient.secondary_insurance) {
          setShowSecondaryInsurance(true);
        }
        if (data.patient.tertiary_insurance) {
          setShowTertiaryInsurance(true);
        }
      } else {
        throw new Error(
          response.error?.message || `Failed to fetch ${subjectLanguageVariants.singular}`,
        );
      }
    } catch (error) {
      console.error('Error fetching patient:', error);
      enqueueSnackbar(
        error instanceof Error
          ? error.message
          : `Failed to load ${subjectLanguageVariants.singular} data`,
        {
          variant: 'error',
        },
      );
      SentryService.logEvent('Error fetching patient', {
        level: 'error',
        tags: {
          patient_id: patientId,
        },
        extra: {
          error: error,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name !== 'payer_id') {
      let processedValue = value;

      if (name === 'state') {
        // Only allow letters, convert to uppercase, and limit to 2 characters
        processedValue = value
          .replace(/[^A-Za-z]/g, '')
          .toUpperCase()
          .slice(0, 2);
      }

      // Clear any existing field errors when user types
      if (fieldErrors[name]) {
        setFieldErrors(prev => ({ ...prev, [name]: '' }));
      }

      // Clear all highlights once the user starts editing
      if (activeHighlights.length > 0) {
        setActiveHighlights([]);
      }

      setPatient(prev => {
        if (!prev) return null;
        const updatedPatient = { ...prev, [name]: processedValue };
        triggerAutosave(updatedPatient);
        return updatedPatient;
      });
    }
  };

  // Helper function to check if patient data has changed
  const hasPatientChanged = (newPatient: Patient, originalPatient: Patient | null): boolean => {
    if (!originalPatient) return true;

    // Compare relevant fields including new ones
    const fieldsToCompare: (keyof Patient)[] = [
      'first_name',
      'middle_name',
      'last_name',
      'preferred_name',
      'identifier',
      'dob',
      'phone',
      'email',
      'gender',
      'sex',
      'pronouns',
      'race',
      'address',
      'city',
      'state',
      'zip',
      'insurance_policy_number',
      'payer_id',
      'payer_name',
      'eligibility',
      'payer_relationship',
      'mobile_phone',
      'employment_status',
      'occupation',
      'patient_consent_on_file',
    ];

    // Also check nested objects
    const hasAddressChanged =
      JSON.stringify(newPatient.primary_address) !==
        JSON.stringify(originalPatient.primary_address) ||
      JSON.stringify(newPatient.secondary_address) !==
        JSON.stringify(originalPatient.secondary_address);

    const hasInsuranceChanged =
      JSON.stringify(newPatient.primary_insurance) !==
        JSON.stringify(originalPatient.primary_insurance) ||
      JSON.stringify(newPatient.secondary_insurance) !==
        JSON.stringify(originalPatient.secondary_insurance) ||
      JSON.stringify(newPatient.tertiary_insurance) !==
        JSON.stringify(originalPatient.tertiary_insurance);

    const hasEmergencyChanged =
      JSON.stringify(newPatient.emergency_contact) !==
      JSON.stringify(originalPatient.emergency_contact);

    return (
      fieldsToCompare.some(field => newPatient[field] !== originalPatient[field]) ||
      hasAddressChanged ||
      hasInsuranceChanged ||
      hasEmergencyChanged
    );
  };

  // Autosave function
  const performSave = useCallback(
    async (patientToSave: Patient) => {
      // Only save if there are actual changes
      if (!hasPatientChanged(patientToSave, originalPatientRef.current)) return;

      setSaveStatus('saving');

      // Validate all fields using Zod schema
      try {
        const validationResult = patientValidationSchema.safeParse(patientToSave);

        if (!validationResult.success) {
          // Extract field errors from Zod validation
          const errors: Record<string, string> = {};
          validationResult.error.errors.forEach(err => {
            const fieldName = err.path[0] as string;
            errors[fieldName] = err.message;
          });

          setFieldErrors(errors);

          // Show first error message
          const firstError = validationResult.error.errors[0];
          enqueueSnackbar(firstError.message, { variant: 'error' });
          setSaveStatus('error');
          // Clear any existing timeout
          if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
          statusTimeoutRef.current = setTimeout(() => setSaveStatus('idle'), 3000);
          return;
        }

        // Clear any existing errors
        setFieldErrors({});

        const accessToken = await getAccessToken();
        const response = await APIService.makeAPIPostRequest({
          requestString: '/patient/update',
          accessToken,
          body: {
            patient_id: patientId,
            updated_fields: patientToSave,
          },
        });

        if (response.ok) {
          setSaveStatus('saved');
          originalPatientRef.current = patientToSave;
          if (onPatientSave) {
            onPatientSave(patientToSave);
          }
        } else {
          throw new Error(
            response.error?.message || `Failed to update ${subjectLanguageVariants.singular}`,
          );
        }
      } catch (error) {
        console.error('Error updating patient:', error);
        setSaveStatus('error');
        enqueueSnackbar(
          error instanceof Error
            ? error.message
            : `Failed to update ${subjectLanguageVariants.singular}`,
          {
            variant: 'error',
          },
        );
        SentryService.logEvent('Error updating patient', {
          level: 'error',
          tags: {
            patient_id: patientId || '',
          },
          extra: {
            error: error,
          },
        });

        // Hide error status after 3 seconds
        // Clear any existing timeout
        if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
        statusTimeoutRef.current = setTimeout(() => setSaveStatus('idle'), 3000);
      }
    },
    [
      getAccessToken,
      onPatientSave,
      patientId,
      enqueueSnackbar,
      subjectLanguageVariants,
      hasPatientChanged,
    ],
  );

  // Trigger autosave with debounce
  const triggerAutosave = useCallback(
    (newPatient: Patient) => {
      // Clear any existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Set new timeout for 3 seconds
      saveTimeoutRef.current = setTimeout(() => {
        performSave(newPatient);
      }, 3000);
    },
    [performSave],
  );

  // Handle blur to save immediately
  const handleBlur = useCallback(() => {
    if (!patient) return;
    // Clear highlights once user interacts
    if (activeHighlights.length > 0) {
      setActiveHighlights([]);
    }
    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    // Save immediately
    performSave(patient);
  }, [patient, performSave, activeHighlights]);

  // Coverage save function - used by useAutoSave
  const performCoverageSave = useCallback(
    async (data: { coverageOrder: number; insurance: InsuranceInfo | PatientCoverage | null }) => {
      const { coverageOrder, insurance } = data;

      try {
        if (!insurance) {
          // Delete coverage if exists
          const existingCoverage =
            coverageOrder === 1
              ? organizedCoverages.primary
              : coverageOrder === 2
                ? organizedCoverages.secondary
                : organizedCoverages.tertiary;

          if (existingCoverage?.patient_insurance_coverage_id) {
            await deleteCoverage({
              patientId,
              coverageId: existingCoverage.patient_insurance_coverage_id,
            }).unwrap();
          }
        } else {
          // Check if this is an update or create
          const existingCoverage =
            coverageOrder === 1
              ? organizedCoverages.primary
              : coverageOrder === 2
                ? organizedCoverages.secondary
                : organizedCoverages.tertiary;

          const coverageData = mapInsuranceToCoverageRequest(coverageOrder, insurance);

          if (existingCoverage?.patient_insurance_coverage_id) {
            await updateCoverage({
              patientId,
              coverageId: existingCoverage.patient_insurance_coverage_id,
              updateCoverageRequest: coverageData,
            }).unwrap();
          } else {
            await createCoverage({
              patientId,
              createCoverageRequest: coverageData,
            }).unwrap();
          }
        }

        // Refetch to get updated data with missing_fields
        await refetchCoverages();
        setHasPendingCoverageChanges(false);
        onCoverageSave?.();
      } catch (error: any) {
        // Log to Sentry but let useAutoSave handle the status
        const errorData = error?.data as ClientErrorResponse | undefined;
        SentryService.logEvent('Error updating coverage', {
          level: 'error',
          tags: {
            patient_id: patientId,
            coverage_order: String(coverageOrder),
            error_code: errorData?.error_code || 'unknown',
          },
          extra: {
            error,
            error_message: errorData?.message,
          },
        });
        throw error; // Re-throw so useAutoSave sets status to 'error'
      }
    },
    [
      organizedCoverages,
      patientId,
      createCoverage,
      updateCoverage,
      deleteCoverage,
      refetchCoverages,
      onCoverageSave,
    ],
  );

  // Use the useAutoSave hook for coverage saves
  const {
    triggerAutoSave: triggerCoverageAutoSave,
    saveImmediately: saveCoverageImmediately,
    status: coverageAutoSaveStatus,
    lastSavedAt: coverageAutoSaveLastSavedAt,
  } = useAutoSave<{ coverageOrder: number; insurance: InsuranceInfo | PatientCoverage | null }>({
    onSave: performCoverageSave,
    debounceMs: 2000, // 2 second debounce as safety net
    flushOnUnmount: true, // Save pending changes when unmounting
  });

  // Track pending coverage data for blur saves
  const pendingCoverageRef = useRef<{
    coverageOrder: number;
    insurance: InsuranceInfo | PatientCoverage | null;
  } | null>(null);

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
      }
    };
  }, []);

  const handlePhoneChange = (newValue: string, field: string) => {
    // For E.164 format fields (new structure)
    if (field === 'mobile_phone') {
      const e164Value = matchIsValidTel(newValue)
        ? newValue.replace(/\D/g, '').startsWith('1')
          ? `+${newValue.replace(/\D/g, '')}`
          : `+1${newValue.replace(/\D/g, '')}`
        : newValue;

      setPatient(prev => {
        if (!prev) return null;
        // Update mobile_phone AND clear legacy phone field to prevent validation conflicts
        const updatedPatient = { 
          ...prev, 
          [field]: e164Value,
          phone: '', // Clear legacy phone field
        };

        if (!e164Value || patientFieldValidators.e164_phone(e164Value).isValid) {
          triggerAutosave(updatedPatient);
          if (fieldErrors[field]) {
            setFieldErrors(prevErrors => ({ ...prevErrors, [field]: '' }));
          }
        } else {
          setFieldErrors(prevErrors => ({
            ...prevErrors,
            [field]: 'Phone must be in E.164 format',
          }));
        }

        return updatedPatient;
      });
    } else {
      // Legacy phone field
      setPatient(prev => {
        if (!prev) return null;
        const updatedPatient = { ...prev, [field]: newValue };

        if (!newValue || matchIsValidTel(newValue)) {
          triggerAutosave(updatedPatient);
          if (fieldErrors[field]) {
            setFieldErrors(prevErrors => ({ ...prevErrors, [field]: '' }));
          }
        } else {
          setFieldErrors(prevErrors => ({
            ...prevErrors,
            [field]: 'Please enter a valid phone number',
          }));
        }

        return updatedPatient;
      });
    }
  };

  // Handle nested object changes
  const handleAddressChange = (
    addressType: 'primary_address' | 'secondary_address',
    address: PatientAddress | null,
  ) => {
    setPatient(prev => {
      if (!prev) return null;
      const updatedPatient = { ...prev, [addressType]: address };
      triggerAutosave(updatedPatient);
      return updatedPatient;
    });
  };

  // Handle insurance/coverage changes - updates local state and triggers debounced save
  const handleInsuranceChange = useCallback(
    (
      insuranceType: 'primary_insurance' | 'secondary_insurance' | 'tertiary_insurance',
      insurance: InsuranceInfo | PatientCoverage | null,
    ) => {
      // Determine coverage order from insurance type
      const coverageOrder =
        insuranceType === 'primary_insurance'
          ? 1
          : insuranceType === 'secondary_insurance'
            ? 2
            : 3;

      // Update local state immediately for responsive UI
      setLocalCoverages(prev => ({
        ...prev,
        [insuranceType === 'primary_insurance'
          ? 'primary'
          : insuranceType === 'secondary_insurance'
            ? 'secondary'
            : 'tertiary']: insurance,
      }));

      // Mark as having pending changes
      setHasPendingCoverageChanges(true);

      // Store pending data for blur save
      pendingCoverageRef.current = { coverageOrder, insurance };

      // Trigger debounced save as safety net (blur will save immediately)
      triggerCoverageAutoSave({ coverageOrder, insurance });
    },
    [triggerCoverageAutoSave],
  );

  // Handle blur on coverage fields - saves immediately
  const handleCoverageBlur = useCallback(() => {
    if (pendingCoverageRef.current && hasPendingCoverageChanges) {
      saveCoverageImmediately(pendingCoverageRef.current);
      pendingCoverageRef.current = null;
    }
  }, [saveCoverageImmediately, hasPendingCoverageChanges]);

  const handleEmergencyContactChange = (contact: EmergencyContact | null) => {
    setPatient(prev => {
      if (!prev) return null;
      const updatedPatient = { ...prev, emergency_contact: contact };
      triggerAutosave(updatedPatient);
      return updatedPatient;
    });
  };

  // Handle language preference change
  const handleLanguageChange = async (event: SelectChangeEvent) => {
    const newLanguage = event.target.value;
    if (newLanguage !== communicationsPreferences?.preferred_language) {
      try {
        await updateLanguage({
          patientId,
          updateLanguagePreferenceRequest: { preferred_language: newLanguage },
        }).unwrap();
        // Refetch to update UI
        await refetchCommunicationsPreferences();
      } catch (err) {
        console.error('Failed to update language preference:', err);
        enqueueSnackbar('Failed to update language preference', { variant: 'error' });
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  if (!patient) {
    return <Typography>No {subjectLanguageVariants.singular} data available.</Typography>;
  }

  const toggleSection = (section: keyof typeof sectionsExpanded) => {
    setSectionsExpanded(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  return (
    <Box>
      {/* Save Status Indicator - Unified */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2, height: '24px' }}>
        <AutosaveStatus
          status={
            // Prioritize active states (saving/error) over saved, then combine
            coverageAutoSaveStatus === 'saving' || saveStatus === 'saving'
              ? 'saving'
              : coverageAutoSaveStatus === 'error' || saveStatus === 'error'
                ? 'error'
                : coverageAutoSaveStatus === 'saved' || saveStatus === 'saved'
                  ? 'saved'
                  : 'idle'
          }
          isDirty={hasPendingCoverageChanges}
          lastSavedAt={coverageAutoSaveLastSavedAt || undefined}
          verbose
          messages={{
            saved: 'Saved',
            saving: 'Saving...',
            error: 'Error saving',
            unsaved: 'Unsaved changes',
          }}
        />
      </Box>

      <Stack spacing={3}>
        {/* Demographics Section */}
        <Paper
          ref={setSectionRef('demographics')}
          elevation={2}
          sx={{
            p: 3,
            transition: 'box-shadow 0.3s ease-in-out',
            '&:hover': {
              boxShadow: 4,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
              cursor: 'pointer',
            }}
            onClick={() => toggleSection('demographics')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                Demographics
              </Typography>
            </Box>
            <IconButton size="small">
              {sectionsExpanded.demographics ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: sectionsExpanded.demographics ? 2 : 0 }}
          >
            Basic personal information and identification
          </Typography>
          <Collapse in={sectionsExpanded.demographics}>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="First Name"
                  name="first_name"
                  value={patient.first_name || ''}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  variant="outlined"
                  size="small"
                  fullWidth
                  error={!!fieldErrors.first_name}
                  helperText={fieldErrors.first_name}
                  sx={{
                    ...(activeHighlights.includes('first_name') && {
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: 2,
                        },
                      },
                    }),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Middle Name"
                  name="middle_name"
                  value={patient.middle_name || ''}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Last Name"
                  name="last_name"
                  value={patient.last_name || ''}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  variant="outlined"
                  size="small"
                  fullWidth
                  error={!!fieldErrors.last_name}
                  helperText={fieldErrors.last_name}
                  sx={{
                    ...(activeHighlights.includes('last_name') && {
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: 2,
                        },
                      },
                    }),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Preferred Name"
                  name="preferred_name"
                  value={patient.preferred_name || ''}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  variant="outlined"
                  size="small"
                  fullWidth
                  helperText="Name the patient prefers to be called"
                  sx={{
                    ...(activeHighlights.includes('preferred_name') && {
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: 2,
                        },
                      },
                    }),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Identifier (MRN/PRN)"
                  name="identifier"
                  value={patient.identifier || ''}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    ...(activeHighlights.includes('identifier') && {
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: 2,
                        },
                      },
                    }),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Date of Birth"
                  name="dob"
                  type="date"
                  value={patient.dob || ''}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  variant="outlined"
                  size="small"
                  fullWidth
                  error={!!fieldErrors.dob}
                  helperText={fieldErrors.dob}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    max: new Date().toISOString().split('T')[0],
                  }}
                  sx={{
                    ...(activeHighlights.includes('dob') && {
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: 2,
                        },
                      },
                    }),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Gender Identity</InputLabel>
                  <Select
                    name="gender"
                    value={patient.gender || ''}
                    onChange={handleSelectChange}
                    onBlur={handleBlur}
                    label="Gender Identity"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="non-binary">Non-binary</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                    <MenuItem value="prefer_not_to_say">Prefer not to say</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sex (for medical records)</InputLabel>
                  <Select
                    name="sex"
                    value={patient.sex || ''}
                    onChange={handleSelectChange}
                    onBlur={handleBlur}
                    label="Sex (for medical records)"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                  </Select>
                  <FormHelperText>Required for insurance claims</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Pronouns"
                  name="pronouns"
                  value={patient.pronouns || ''}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  variant="outlined"
                  size="small"
                  fullWidth
                  placeholder="e.g., he/him, she/her, they/them"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Race/Ethnicity"
                  name="race"
                  value={patient.race || ''}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              </Grid>
            </Grid>
          </Collapse>
        </Paper>

        {/* Contact Information Section */}
        <Paper
          ref={setSectionRef('contact')}
          elevation={2}
          sx={{
            p: 3,
            transition: 'box-shadow 0.3s ease-in-out',
            '&:hover': {
              boxShadow: 4,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
              cursor: 'pointer',
            }}
            onClick={() => toggleSection('contact')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PhoneIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                Contact Information
              </Typography>
            </Box>
            <IconButton size="small">
              {sectionsExpanded.contact ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: sectionsExpanded.contact ? 2 : 0 }}
          >
            Primary phone and email for patient communication
          </Typography>
          <Collapse in={sectionsExpanded.contact}>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <MuiTelInput
                  label="Mobile Phone"
                  value={patient.mobile_phone || patient.phone || ''}
                  onChange={newValue =>
                    handlePhoneChange(
                      newValue,
                      patient.mobile_phone !== undefined ? 'mobile_phone' : 'phone',
                    )
                  }
                  onBlur={handleBlur}
                  defaultCountry="US"
                  forceCallingCode
                  onlyCountries={['US', 'CA', 'GB', 'AU', 'NZ', 'ZA', 'MX']}
                  size="small"
                  error={!!fieldErrors.mobile_phone || !!fieldErrors.phone}
                  helperText={fieldErrors.mobile_phone || fieldErrors.phone || undefined}
                  sx={{
                    width: '100%',
                    '& input': {
                      height: '1.4375em',
                    },
                    ...((activeHighlights.includes('mobile_phone') ||
                      activeHighlights.includes('phone')) && {
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: 2,
                        },
                      },
                    }),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  value={patient.email || ''}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  variant="outlined"
                  size="small"
                  fullWidth
                  error={!!fieldErrors.email}
                  helperText={fieldErrors.email}
                  sx={{
                    ...(activeHighlights.includes('email') && {
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: 2,
                        },
                      },
                    }),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Preferred Language</InputLabel>
                  <Select
                    value={communicationsPreferences?.preferred_language || ''}
                    onChange={handleLanguageChange}
                    label="Preferred Language"
                    disabled={isUpdatingLanguage}
                  >
                    <MenuItem value="">
                      <em>Not set</em>
                    </MenuItem>
                    {LANGUAGES.map(lang => (
                      <MenuItem key={lang.code} value={lang.code}>
                        {lang.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Contact Preferences
                  </Typography>
                  <PatientCommunicationPreferences
                    patientId={patientId}
                    mobilePhone={patient.mobile_phone || patient.phone}
                    email={patient.email}
                  />
                </Box>
              </Grid>
            </Grid>
          </Collapse>
        </Paper>

        {/* Address Information Section */}
        <Paper
          ref={setSectionRef('addresses')}
          elevation={2}
          sx={{
            p: 3,
            transition: 'box-shadow 0.3s ease-in-out',
            '&:hover': {
              boxShadow: 4,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
              cursor: 'pointer',
            }}
            onClick={() => toggleSection('addresses')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <HomeIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                Address Information
              </Typography>
            </Box>
            <IconButton size="small">
              {sectionsExpanded.addresses ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: sectionsExpanded.addresses ? 2 : 0 }}
          >
            Residential and mailing addresses
          </Typography>
          <Collapse in={sectionsExpanded.addresses}>
            <Divider sx={{ mb: 3 }} />

            {/* Primary Address or Legacy Fields */}
            <AddressCard
              address={
                patient.primary_address ||
                (patient.address
                  ? {
                      line1: patient.address,
                      city: patient.city,
                      state: patient.state,
                      zip: patient.zip,
                      phone: null,
                    }
                  : null)
              }
              onChange={address => handleAddressChange('primary_address', address)}
              onBlur={handleBlur}
              title="Primary Address"
              errors={fieldErrors}
              highlightedFields={activeHighlights}
            />

            {/* Secondary Address */}
            {showSecondaryAddress && (
              <Box sx={{ mt: 3 }}>
                <AddressCard
                  address={patient.secondary_address}
                  onChange={address => handleAddressChange('secondary_address', address)}
                  onBlur={handleBlur}
                  title="Secondary/Mailing Address"
                  showDelete
                  onDelete={() => {
                    setShowSecondaryAddress(false);
                    handleAddressChange('secondary_address', null);
                  }}
                  errors={fieldErrors}
                  highlightedFields={activeHighlights}
                />
              </Box>
            )}

            {/* Add Secondary Address Button */}
            {!showSecondaryAddress && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setShowSecondaryAddress(true)}
                >
                  Add Secondary Address
                </Button>
              </Box>
            )}
          </Collapse>
        </Paper>

        {/* Insurance Information Section */}
        <Paper
          ref={setSectionRef('insurance')}
          elevation={2}
          sx={{
            p: 3,
            transition: 'box-shadow 0.3s ease-in-out',
            '&:hover': {
              boxShadow: 4,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
              cursor: 'pointer',
            }}
            onClick={() => toggleSection('insurance')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PaymentIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                Insurance Information
              </Typography>
            </Box>
            <IconButton size="small">
              {sectionsExpanded.insurance ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: sectionsExpanded.insurance ? 2 : 0 }}
          >
            Primary, secondary, and tertiary insurance coverage
          </Typography>
          <Collapse in={sectionsExpanded.insurance}>
            <Divider sx={{ mb: 3 }} />

            {/* Primary Insurance */}
            <>
              {/* Show pending status if coverage has missing fields */}
              {localCoverages.primary &&
                'status' in localCoverages.primary &&
                localCoverages.primary.status === 'pending' &&
                localCoverages.primary.missing_fields &&
                localCoverages.primary.missing_fields.length > 0 && (
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.5,
                      bgcolor: 'warning.light',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 500 }}>
                      ⚠️ Coverage is pending. Missing:{' '}
                      {localCoverages.primary.missing_fields
                        .map(field => {
                          const labels: Record<string, string> = {
                            member_id: 'Member/Policy ID',
                            payer_name: 'Payer Name',
                            relationship_to_subscriber: 'Relationship',
                          };
                          return labels[field] || field;
                        })
                        .join(', ')}
                    </Typography>
                  </Box>
                )}

              <InsuranceCard
                insurance={localCoverages.primary ?? null}
                onChange={insurance => handleInsuranceChange('primary_insurance', insurance)}
                onBlur={handleCoverageBlur}
                title="Primary Insurance"
                patientId={patientId}
                errors={fieldErrors}
                highlightedFields={activeHighlights}
              />
            </>

            {/* Secondary Insurance */}
            {showSecondaryInsurance && (
              <Box sx={{ mt: 3 }}>
                {/* Show pending status if coverage has missing fields */}
                {localCoverages.secondary &&
                  'status' in localCoverages.secondary &&
                  localCoverages.secondary.status === 'pending' &&
                  localCoverages.secondary.missing_fields &&
                  localCoverages.secondary.missing_fields.length > 0 && (
                    <Box
                      sx={{
                        mb: 2,
                        p: 1.5,
                        bgcolor: 'warning.light',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 500 }}>
                        ⚠️ Coverage is pending. Missing:{' '}
                        {localCoverages.secondary.missing_fields
                          .map(field => {
                            const labels: Record<string, string> = {
                              member_id: 'Member/Policy ID',
                              payer_name: 'Payer Name',
                              relationship_to_subscriber: 'Relationship',
                            };
                            return labels[field] || field;
                          })
                          .join(', ')}
                      </Typography>
                    </Box>
                  )}

                <InsuranceCard
                  insurance={localCoverages.secondary ?? null}
                  onChange={insurance => handleInsuranceChange('secondary_insurance', insurance)}
                  onBlur={handleCoverageBlur}
                  title="Secondary Insurance"
                  patientId={patientId}
                  showDelete
                  onDelete={() => {
                    setShowSecondaryInsurance(false);
                    handleInsuranceChange('secondary_insurance', null);
                  }}
                  errors={fieldErrors}
                  highlightedFields={activeHighlights}
                />
              </Box>
            )}

            {/* Tertiary Insurance */}
            {showTertiaryInsurance && (
              <Box sx={{ mt: 3 }}>
                {/* Show pending status if coverage has missing fields */}
                {localCoverages.tertiary &&
                  'status' in localCoverages.tertiary &&
                  localCoverages.tertiary.status === 'pending' &&
                  localCoverages.tertiary.missing_fields &&
                  localCoverages.tertiary.missing_fields.length > 0 && (
                    <Box
                      sx={{
                        mb: 2,
                        p: 1.5,
                        bgcolor: 'warning.light',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 500 }}>
                        ⚠️ Coverage is pending. Missing:{' '}
                        {localCoverages.tertiary.missing_fields
                          .map(field => {
                            const labels: Record<string, string> = {
                              member_id: 'Member/Policy ID',
                              payer_name: 'Payer Name',
                              relationship_to_subscriber: 'Relationship',
                            };
                            return labels[field] || field;
                          })
                          .join(', ')}
                      </Typography>
                    </Box>
                  )}

                <InsuranceCard
                  insurance={localCoverages.tertiary ?? null}
                  onChange={insurance => handleInsuranceChange('tertiary_insurance', insurance)}
                  onBlur={handleCoverageBlur}
                  title="Tertiary Insurance"
                  patientId={patientId}
                  showDelete
                  onDelete={() => {
                    setShowTertiaryInsurance(false);
                    handleInsuranceChange('tertiary_insurance', null);
                  }}
                  errors={fieldErrors}
                  highlightedFields={activeHighlights}
                />
              </Box>
            )}

            {/* Add Insurance Buttons */}
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              {!showSecondaryInsurance && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setShowSecondaryInsurance(true)}
                >
                  Add Secondary Insurance
                </Button>
              )}
              {showSecondaryInsurance && !showTertiaryInsurance && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setShowTertiaryInsurance(true)}
                >
                  Add Tertiary Insurance
                </Button>
              )}
            </Box>
          </Collapse>
        </Paper>

        {/* Emergency Contact Section */}
        <Paper
          ref={setSectionRef('emergency')}
          elevation={2}
          sx={{
            p: 3,
            transition: 'box-shadow 0.3s ease-in-out',
            '&:hover': {
              boxShadow: 4,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
              cursor: 'pointer',
            }}
            onClick={() => toggleSection('emergency')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ContactEmergencyIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                Emergency Contact
              </Typography>
            </Box>
            <IconButton size="small">
              {sectionsExpanded.emergency ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: sectionsExpanded.emergency ? 2 : 0 }}
          >
            Contact information for emergencies
          </Typography>
          <Collapse in={sectionsExpanded.emergency}>
            <Divider sx={{ mb: 3 }} />
            <EmergencyContactCard
              contact={patient.emergency_contact}
              onChange={handleEmergencyContactChange}
              onBlur={handleBlur}
              errors={fieldErrors}
              highlightedFields={activeHighlights}
            />
          </Collapse>
        </Paper>

        {/* Employment Information Section */}
        <Paper
          ref={setSectionRef('employment')}
          elevation={2}
          sx={{
            p: 3,
            transition: 'box-shadow 0.3s ease-in-out',
            '&:hover': {
              boxShadow: 4,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
              cursor: 'pointer',
            }}
            onClick={() => toggleSection('employment')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <WorkIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                Employment Information
              </Typography>
            </Box>
            <IconButton size="small">
              {sectionsExpanded.employment ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: sectionsExpanded.employment ? 2 : 0 }}
          >
            Employment status and occupation details
          </Typography>
          <Collapse in={sectionsExpanded.employment}>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Employment Status</InputLabel>
                  <Select
                    name="employment_status"
                    value={patient.employment_status || ''}
                    onChange={handleSelectChange}
                    onBlur={handleBlur}
                    label="Employment Status"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value="employed">Employed</MenuItem>
                    <MenuItem value="self-employed">Self-Employed</MenuItem>
                    <MenuItem value="unemployed">Unemployed</MenuItem>
                    <MenuItem value="retired">Retired</MenuItem>
                    <MenuItem value="student">Student</MenuItem>
                    <MenuItem value="disabled">Disabled</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Occupation"
                  name="occupation"
                  value={patient.occupation || ''}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={patient.patient_consent_on_file || false}
                      onChange={e => {
                        setPatient(prev => {
                          if (!prev) return null;
                          const updatedPatient = {
                            ...prev,
                            patient_consent_on_file: e.target.checked,
                          };
                          triggerAutosave(updatedPatient);
                          return updatedPatient;
                        });
                      }}
                    />
                  }
                  label="Patient consent/authorization signature on file"
                />
              </Grid>
            </Grid>
          </Collapse>
        </Paper>
      </Stack>
    </Box>
  );
};

export default PatientInformationView;
